import EngineShell, { type SubTabDef } from '../components/EngineShell';
import DataIntervention, {
  ActionIntervention, MetricTiles,
} from '../components/engine/DataIntervention';
import { api } from '../api/client';
import { useState } from 'react';
import Engine6_MnA from './Engine6_MnA';

/**
 * Engine 7 · Corporate Development / M&A (CPG value chain · stage 7).
 * Inorganic capacity + resilience lever. Capacity Acquisition is the lighthouse.
 */

// diligence/run shape was not captured live — render defensively.
interface DiligenceResp { [k: string]: unknown }

interface FreightRedundancyResp { redundancy_score: number; west_coast_gain: string; freight_saving_usd_M: number }

interface PartnershipResp { structure: string; licensing_terms: string[]; fit_score: number }

interface IntegrationResp { active_deals: unknown[]; milestones: unknown[]; status?: string }

const moneyM = (v: number) => (v < 0 ? `−$${Math.abs(v).toFixed(1)}M` : `$${v.toFixed(1)}M`);
const fmtNum = (v: unknown) => (typeof v === 'number' ? (Number.isInteger(v) ? v.toLocaleString() : v.toFixed(2)) : v == null ? '—' : String(v));

export default function Engine7Shell() {
  const subTabs: SubTabDef[] = [
    {
      id: 'capacity_acquisition', name: 'Capacity Acquisition', tier: 'lighthouse', moat: 'core', enabled: true,
      render: () => <Engine6_MnA />,
    },
    {
      id: 'asset_diligence', name: 'Asset Diligence', tier: 'flagship', moat: 'core', enabled: true,
      render: () => <AssetDiligence />,
    },
    {
      id: 'freight_redundancy', name: 'Freight Redundancy', tier: 'flagship', moat: 'core', enabled: true,
      render: () => <FreightRedundancy />,
    },
    {
      id: 'partnership', name: 'Partnership Structuring', tier: 'flagship', moat: 'core', enabled: true,
      render: () => <Partnership />,
    },
    {
      id: 'integration_tracker', name: 'Integration Tracker', tier: 'flagship', moat: 'commodity', enabled: true,
      render: () => (
        <DataIntervention<IntegrationResp>
          objective="O7 · track post-close integration milestones (hygiene)"
          workflow={['Pull active deals', 'Track integration milestones', 'Flag slipping milestones', 'Report status']}
          decisionRules={{ rules: ['Flag any slipped milestone', 'Escalate stalled integrations'] }}
          aiLogic={{ description: 'Deterministic integration milestone tracker.', modality: 'deterministic', hitlLevel: 'L1 sampling', moat: 'commodity' }}
          kpi={{ primary: 'Active deals + milestone status', owner: 'Corp Dev / PMO' }}
          queryKey={['mna', 'integration']} fetcher={() => api.get<IntegrationResp>('/api/mna/integration')}
          render={(d) => (
            <>
              <MetricTiles tiles={[
                { label: 'Active deals', value: String(d.active_deals.length), color: d.active_deals.length ? 'ink' : 'muted' },
                { label: 'Milestones', value: String(d.milestones.length), color: d.milestones.length ? 'ink' : 'muted' },
                ...(d.status ? [{ label: 'Status', value: d.status, color: 'muted' as const }] : []),
              ]} />
              {d.active_deals.length === 0 && <div className="small faint">No active deals.</div>}
              {d.milestones.length === 0 && <div className="small faint">No milestones.</div>}
            </>
          )}
        />
      ),
    },
  ];

  return (
    <EngineShell
      engineId={7}
      engineName="Engine 7 · Corporate Development / M&A"
      engineCode="E7"
      subTabs={subTabs}
      backgrounds={[{ id: 'market-scan', name: 'Market Scan', status: 'ok' }]}
    />
  );
}

/* Asset Diligence — POST with a target id. Shape not captured live → render defensively. */
function AssetDiligence() {
  const [targetId, setTargetId] = useState('MNA-01');
  return (
    <ActionIntervention<DiligenceResp>
      objective="O7 · run diligence on an acquisition target"
      workflow={['Enter a target id', 'Run diligence model', 'Read capacity + freight + payback', 'Stage the recommendation']}
      decisionRules={{ rules: ['Prioritize capacity gain with short payback', 'Flag any brand-acquisition entanglement'] }}
      aiLogic={{ description: 'Deterministic diligence model over the target file.', modality: 'deterministic', hitlLevel: 'L4 human decision', moat: 'core' }}
      kpi={{ primary: 'Capacity gain + freight saving + payback', owner: 'Corp Dev / CFO' }}
      initialState={{ capacity_gain: '18M cases', freight_saving: '$2.1M', payback: '2.4 years', recommendation: 'GO asset-only' }}
      input={
        <>
          <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Target id</div>
          <input className="c2-input" value={targetId} onChange={(e) => setTargetId(e.target.value)} style={{ width: 200 }} />
        </>
      }
      runLabel="Run diligence"
      action={() => api.post<DiligenceResp>('/api/mna/diligence/run', { target_id: targetId })}
      render={(d) => (
        <MetricTiles tiles={[
          { label: 'Capacity gain', value: fmtNum(d.capacity_gain), color: 'olive' },
          { label: 'Freight saving', value: fmtNum(d.freight_saving), color: 'olive' },
          { label: 'Payback', value: fmtNum(d.payback), color: 'ink' },
        ]} />
      )}
    />
  );
}

/* Freight Redundancy — POST with a target id */
function FreightRedundancy() {
  const [targetId, setTargetId] = useState('MNA-01');
  return (
    <ActionIntervention<FreightRedundancyResp>
      objective="O7 · score how much a target de-risks freight and adds west-coast capacity"
      workflow={['Enter a target id', 'Score freight redundancy', 'Size west-coast gain + savings', 'Rank against other targets']}
      decisionRules={{ rules: ['Prioritize high redundancy with real freight savings', 'Weight hurricane-buffered capacity'] }}
      aiLogic={{ description: 'Deterministic redundancy scoring from the freight network.', modality: 'deterministic', hitlLevel: 'L3 approval gating', moat: 'core' }}
      kpi={{ primary: 'Redundancy score + west-coast gain + freight saving ($M)', owner: 'Corp Dev / Logistics' }}
      initialState={{ redundancy_score: 0.82, west_coast_gain: 0.18, freight_saving_usd_M: 2.1 }}
      input={
        <>
          <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Target id</div>
          <input className="c2-input" value={targetId} onChange={(e) => setTargetId(e.target.value)} style={{ width: 200 }} />
        </>
      }
      runLabel="Score redundancy"
      action={() => api.mnaFreightRedundancy(targetId) as Promise<FreightRedundancyResp>}
      render={(d) => (
        <MetricTiles tiles={[
          { label: 'Redundancy score', value: (d.redundancy_score * 100).toFixed(0), color: 'olive' },
          { label: 'West-coast gain', value: d.west_coast_gain, color: 'ink' },
          { label: 'Freight saving', value: moneyM(d.freight_saving_usd_M), color: 'olive' },
        ]} />
      )}
    />
  );
}

/* Partnership Structuring — POST with a partner */
function Partnership() {
  const [partner, setPartner] = useState('West Coast co-packer');
  return (
    <ActionIntervention<PartnershipResp>
      objective="O7 · structure a partnership when full acquisition is wrong"
      workflow={['Enter a partner', 'Propose a deal structure', 'Set licensing terms', 'Score strategic fit']}
      decisionRules={{ rules: ['Hold a no-brand-acquisition covenant', 'Reserve capacity with audit rights'] }}
      aiLogic={{ description: 'Deterministic partnership structuring with term scoring.', modality: 'deterministic', hitlLevel: 'L4 human decision', moat: 'core' }}
      kpi={{ primary: 'Structure + licensing terms + fit score', owner: 'Corp Dev / Legal' }}
      initialState={{ structure: 'Capacity-reservation partnership', licensing_terms: ['audit rights'], fit_score: 78 }}
      input={
        <>
          <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Partner</div>
          <input className="c2-input" value={partner} onChange={(e) => setPartner(e.target.value)} style={{ width: 280 }} />
        </>
      }
      runLabel="Structure partnership"
      action={() => api.mnaPartnership(partner) as Promise<PartnershipResp>}
      render={(d) => (
        <>
          <MetricTiles tiles={[
            { label: 'Structure', value: d.structure, color: 'ink' },
            { label: 'Fit score', value: String(d.fit_score), color: d.fit_score >= 70 ? 'olive' : 'gold' },
          ]} />
          <div className="small faint" style={{ marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 11 }}>Licensing terms</div>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {d.licensing_terms.map((t) => <span key={t} className="tag">{t}</span>)}
          </div>
        </>
      )}
    />
  );
}
